import { DateString as DateStringInner } from "@vehicle-contract/common/lib/model/common/DateString";
import { ContractingParty as ContractingPartyInner } from "@vehicle-contract/common/lib/model/contract/ContractingParty";
import { ContractSource } from "@vehicle-contract/common/lib/model/contract/ContractSource";
import { CoOwner } from "@vehicle-contract/common/lib/model/contract/CoOwner";
import { CustomerReference } from "@vehicle-contract/common/lib/model/contract/CustomerReference";
import { Role } from "@vehicle-contract/common/lib/model/contract/Role";
import {
  SellerAdditionalInformation,
  BuyerAdditionalInformation,
  AttachmentsInfo,
  ContractType,
  InvitationType,
  SettlementFields,
  SignatureStatus,
  TransactionInfo,
} from "@vehicle-contract/common/lib/model/contract/SalesContract";
import { SchemaType } from "@vehicle-contract/common/lib/model/contract/SchemaType";
import { SigningMethod } from "@vehicle-contract/common/lib/model/contract/SigningMethod";
import { SigningParty } from "@vehicle-contract/common/lib/model/contract/SigningParty";
import { VehicleData } from "@vehicle-contract/common/lib/model/contract/VehicleData";
import { CarInsurance } from "@vehicle-contract/common/lib/model/insurance/CarInsurance";
import { Consent } from "@vehicle-contract/common/lib/model/membership/Consent";
import { SettlementStatusInfo } from "../../../../common/lib/model/contract/Settlement";
import { Message } from "../../admin/contract-details/Messages";
import { OwnershipTransferInfoViewModel } from "../ownership-transfer/OwnershipTransfer";

export { SignatureStatus, SigningMethod, Role, ContractType };

export type ContractingParty = ContractingPartyInner;
export type DateString = DateStringInner;

export interface AutoTechVehicleViewModel {
  colorName: string;
  isElectric: boolean;
  manufactureName: string;
  modelName: string;
  typeName: string;
  year: number;
  vehicleTypeShortName?: string;
  registeredDate?: DateString;
  licensePlateNumber: string;
}

interface TrialMembershipCustomerReference {
  customerId: string;
  customerGuid: string;
}

interface TrialMembershipActivation {
  customerReference: TrialMembershipCustomerReference;
  activated?: DateString;
}

export interface TrialMembership {
  accepted: boolean;
  acceptedTestTrial: boolean;
  consentValues: Consent[];
  updated: string;
  activation: TrialMembershipActivation;
}

export interface ContractUpgradePaymentViewModel {
  initiator: Role;
  isPaid: boolean;
}

export interface ContractUpgradePaymentInitiatorViewModel
  extends ContractUpgradePaymentViewModel {
  paymentId: string;
  contractType: ContractType;
  customer: TrialMembershipCustomerReference;
}

export interface ContractTypeInfoViewModel {
  isRequired: boolean;
  current: ContractType;
  payment:
    | ContractUpgradePaymentViewModel
    | ContractUpgradePaymentInitiatorViewModel;
  upgradedCustomerId?: string;
  history: ContractTypeSelectionViewModel[];
}

export interface ContractTypeSelectionViewModel {
  contractType: ContractType;
  created: DateString;
  role?: Role;
}

export interface SigningTaskViewModel {
  role: Role;
  signingParty: SigningParty;
  signatureStatus: SignatureStatus;
  token: string;
  updated?: DateString;
  invitationHistory: Message[];
}

export interface SignatureInfoViewModel {
  hash: string;
  tasks: SigningTaskViewModel[];
  signingMethod: SigningMethod;
  signatureStatus: SignatureStatus;
  signedDocumentReady: boolean;
  hasSignedOwnershipTransfer: boolean;
  includesOwnershipTransfer: boolean;
  initiator: Role;
  completed?: DateString;
  created: DateString;
}

export interface FieldValue {
  boolValue?: boolean;
  intValue?: number;
  stringValue?: string;
  decimalValue?: number;
  dateTimeValue?: DateString;
}

export interface InvitationViewModel {
  sent: string;
  recipient: Role;
  recipientPhone: string;
  recipientEmail: string;
  type: InvitationType;
  accepted: boolean;
  acceptedTime: DateString;
}

interface Condition {
  values: { [id: string]: FieldValue };
}

export type OtherContractingPartyViewModel = Omit<
  ContractingParty,
  "nationalIdentityNumber"
>;

interface BaseContract {
  id: string;
  documentHash: string;
  initiator: Role;
  licensePlateNumber: string;
  schemaType: SchemaType;
  source: ContractSource;
  created: DateString;
  vehicleData: VehicleData | null;
  autoTechData: AutoTechVehicleViewModel | null;
  buyerCoOwner: CoOwner | null;
  sellerCoOwner: CoOwner | null;
  condition: Condition;
  signature: SignatureInfoViewModel | null;
  invitation: InvitationViewModel | null;
  attachments: AttachmentsInfo | null;
  financialAttachments: AttachmentsInfo | null;
  settlement: SettlementFields | null;
  transaction: TransactionInfo | null;
  contractType: ContractTypeInfoViewModel | null;
  customerReferences: CustomerReference[];
  ownershipTransfer: OwnershipTransferInfoViewModel | null;
  sellerAdditionalInformation: SellerAdditionalInformation | null;
  buyerAdditionalInformation: BuyerAdditionalInformation | null;
  settlementStatus: SettlementStatusInfo | null;
}

export interface BuyerContract extends BaseContract {
  buyerToken: string;
  buyerUrl: string;
  buyerTrialMembership: TrialMembership | null;
  buyerCarInsurance: CarInsurance | null;
  buyer: ContractingParty | null;
  seller: OtherContractingPartyViewModel | null;
}

export interface SellerContract extends BaseContract {
  sellerToken: string;
  sellerTrialMembership: TrialMembership | null;
  sellerUrl: string;
  buyer: OtherContractingPartyViewModel | null;
  seller: ContractingParty | null;
}

export type ContractViewModel = SellerContract | BuyerContract;
